import type { Ref } from 'vue';
import { computed } from 'vue';
import type { IMpResolvedTicket, IPopularTicketKey } from '@/interface/IPopularMixParlay';
import { LeagueSize, MarketPage, SportType } from '@/interface/enum';
import { useEventQuery } from '@/libraries/oddsApi/composables/useEventQuery';
import { useOdds } from '@/libraries/oddsApi/composables/useOdds';
import { EnumPresetFilterType } from '@/types';

export function useMpTicketState(ticketKey: IPopularTicketKey, pollInterval: Ref<number>) {
    const { event, loaded: eventLoaded, error: eventError } = useEventQuery(ticketKey.eventId, true, true, pollInterval);
    const sportType = computed(() => event.value?.sportType ?? SportType.Unknown);
    const leagueSize = computed(() => event.value?.leagueSize ?? LeagueSize.Small);
    const isOddsEnabled = computed(() => !!event.value && !event.value.isClosed);
    const isLive = computed(() => event.value?.marketPage === MarketPage.Live);

    const { oddsList, loaded: oddsLoaded, error: oddsError } = useOdds({
        eventId: ticketKey.eventId,
        oddsId: ticketKey.oddsId,
        isMixParlayMode: true,
        presetFilter: EnumPresetFilterType.All,
        isEnabled: isOddsEnabled,
        sportType,
        leagueSize,
        isLive,
    });

    const odds = computed(() => oddsList.value.find(item => item.oddsId === ticketKey.oddsId) ?? null);
    const priceOption = computed(() => odds.value?.priceOption.find(option => option.option.toLowerCase() === ticketKey.option.toLowerCase()) ?? null);
    const hasError = computed(() => !!eventError.value || !!oddsError.value);
    const isResolved = computed(() => hasError.value
        || (eventLoaded.value && (!isOddsEnabled.value || oddsLoaded.value)));
    const isValid = computed(() => isResolved.value && !hasError.value && isOddsEnabled.value && !!odds.value && !!priceOption.value);
    const ticket = computed<IMpResolvedTicket>(() => ({
        ...ticketKey,
        event: event.value,
        odds: odds.value,
        priceOption: priceOption.value,
    }));

    return { ticket, isResolved, isValid };
}
